import React, { ReactElement, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Col, Row } from 'idsk-react-components';
import { getTitle, setTitle } from 'slovlex-idsk-react-components';
import {
  createLegMaterial,
  validateLegMaterial,
} from '../../../client/legislativnyMaterial/legislativnyMaterialCreateValidateClient';
import { useClient, useClientStatusContext } from '../../../context/ClientStatusContext';
import { useSuccessMessageContext } from '../../../context/SuccessMessageContext';
import LegislativnyMaterial
  from '../../../model/legislativnyMaterial/LegislativnyMaterialCreateUpdate';
import { SafeParameterSetter } from '../../../types/SafeParameterSetter';
import useFieldErrorMessages from '../../useFieldErrorMessages';
import TypMaterialuStepOne from './legMaterialStepPages/TypMaterialuStepOne';
import BasicDataStepTwo from './legMaterialStepPages/BasicDataStepTwo';
import LegMaterialCreateStepEnum, {
  stepValues,
} from '../../../model/legislativnyMaterial/LegMaterialCreateStepEnum';
import routes from '../../../routing/routes';
import RelatedDataStepThree from './legMaterialStepPages/RelatedDataStepThree';
import PredkladatelStepFour from './legMaterialStepPages/PredkladatelStepFour';
import CheckDataStepFive from './legMaterialStepPages/CheckDataStepFive';
import './legMaterialCreate.scss';
import PolozkaCiselnik from '../../../model/ciselniky/PolozkaCiselnik';
import { SubjectItem } from '../../../model/legislativnyMaterial/SubjectItem';
import splitCisloLegMaterialu from '../../../function/splitCisloLegMaterialu';
import { useUserContext } from '../../../context/UserContext';
import scrollToTop from '../../../function/scrollToTop';
import NavigationGuard from '../../NavigationGuardComponent/components/NavigationGuard';

interface Props {
  setPageStatus?: (status: number) => void;
}

const LegislativnyMaterialCreate = ({ setPageStatus }: Props): ReactElement => {
  const navigate = useNavigate();
  const { displaySuccess } = useSuccessMessageContext();
  const { clearStatus } = useClientStatusContext();
  const { pouzivatel } = useUserContext();
  const [blocked, setBlocked] = useState(false);
  const [readyToSubmit, setReadyToSubmit] = useState(false);

  const [legislativnyMaterial, setLegislativnyMaterial] = useState<LegislativnyMaterial>({
    oblastList: [],
    typ: {} as PolozkaCiselnik,
    nazov: '',
    dalsiPredkladatelia: [{
      organizacia: null,
      predkladatel: null,
    }],
    rezortneCislo: '',
    podnet: '',
    suvisiaceProcesy: [],
    zodpovednaInstitucia: {
      uuid: pouzivatel.organizaciaUuid,
      nazov: pouzivatel.organizaciaNazov,
      uplnyNazov: `${pouzivatel.organizaciaSkratka} (${pouzivatel.organizaciaNazov})`,
      ldapUuid: pouzivatel.organizaciaUuid,
    },
    zodpovednyPredkladatel: {} as SubjectItem,
    hlavnyNovelizovanyPredpis: null,
    novelizovanePredpisy: [],
    activeStep: LegMaterialCreateStepEnum.TYP_MATERIALU_1,
  });
  const [predkladatelMap, setPredkladatelMap] = useState(new Map<string, Array<{
    uuid: string,
    nazov: string
  }>>());
  const updatePredkladatelMap = (data): void => {
    setPredkladatelMap(data);
  };

  useEffect(() => {
    if (Object.keys(legislativnyMaterial.typ).length !== 0) {
      setBlocked(true);
      window.parent.postMessage({
        type: 'block-navigate',
        value: true,
        heading: 'Naozaj si prajete ukončiť vytváranie procesu?',
        content: 'Po ukončení vytvárania procesu prídete o zadané údaje vytváraného procesu.',
      }, '*');
    }
  }, [legislativnyMaterial.typ]);

  useEffect(() => {
    setTitle(getTitle());
  }, [legislativnyMaterial.activeStep]);

  const {
    getFieldErrorMessage,
    clearFieldErrorMessage,
  } = useFieldErrorMessages();
  const {
    wrapClient: validateLegMaterialClient,
    status: validateLegMaterialStatus,
  } = useClient(validateLegMaterial);
  const {
    wrapClient: createLegMaterialClient,
    status: createLegMaterialStatus,
  } = useClient(createLegMaterial);

  const updateField: SafeParameterSetter<LegislativnyMaterial> = (fieldName, value) => {
    setLegislativnyMaterial(prevState => ({
      ...prevState,
      [fieldName]: value,
    }));
    clearFieldErrorMessage(fieldName);
  };

  useEffect(() => {
    setPageStatus(createLegMaterialStatus.responseCode);
  }, [createLegMaterialStatus.responseCode, setPageStatus]);

  const onChangeStep = async (
    nextStep: LegMaterialCreateStepEnum,
  ): Promise<void> => {
    if (legislativnyMaterial.activeStep === LegMaterialCreateStepEnum.CHECK_DATA_5
        || legislativnyMaterial.activeStep === LegMaterialCreateStepEnum.RELATED_DATA_3
        || stepValues[legislativnyMaterial.activeStep] > stepValues[nextStep]) {
      updateField('activeStep', nextStep);
    } else {
      await validateLegMaterialClient(legislativnyMaterial);
      updateField('activeStep', nextStep);
    }
    scrollToTop();
    clearStatus();
  };

  const [type, setType] = useState('');
  const [year, setYear] = useState(null);
  const [index, setIndex] = useState(null);

  const handleSubmit = async (): Promise<void> => {
    await createLegMaterialClient(legislativnyMaterial)
      .then(response => {
        const splitResult = splitCisloLegMaterialu(response.data);
        setType(splitResult.type);
        setYear(splitResult.year);
        setIndex(splitResult.index);
        setBlocked(false);
        setReadyToSubmit(true);
        window.parent.postMessage({
          type: 'block-navigate',
          value: false,
        }, '*');
      });
    setTimeout(() => {
      displaySuccess('Legislatívny proces bol úspešne uložený.');
    }, 300);
  };

  useEffect(() => {
    if (blocked === false && readyToSubmit) {
      navigate(routes.procesy.detail.withParams(type, year, index));
    }
  }, [blocked, displaySuccess, navigate, type, year, index, readyToSubmit]);

  const renderActiveStepPage = (): ReactElement => {
    switch (legislativnyMaterial.activeStep) {
      case LegMaterialCreateStepEnum.BASIC_DATA_2:
        return (
          <BasicDataStepTwo
            onChangeStep={onChangeStep}
            legislativnyMaterial={legislativnyMaterial}
            onUpdateField={updateField}
            loading={validateLegMaterialStatus.loading}
            getFieldErrorMessage={getFieldErrorMessage}
          />
        );
      case LegMaterialCreateStepEnum.RELATED_DATA_3:
        return (
          <RelatedDataStepThree
            onChangeStep={onChangeStep}
            legislativnyMaterial={legislativnyMaterial}
            onUpdateField={updateField}
            loading={validateLegMaterialStatus.loading}
          />
        );
      case LegMaterialCreateStepEnum.PREDKLADATEL_4:
        return (
          <PredkladatelStepFour
            onChangeStep={onChangeStep}
            legislativnyMaterial={legislativnyMaterial}
            onUpdateField={updateField}
            onPredkladatelMapUpdate={updatePredkladatelMap}
            predkladatelMap={predkladatelMap}
            loading={validateLegMaterialStatus.loading}
            getFieldErrorMessage={getFieldErrorMessage}
          />
        );
      case LegMaterialCreateStepEnum.CHECK_DATA_5:
        return (
          <CheckDataStepFive
            onChangeStep={onChangeStep}
            legislativnyMaterial={legislativnyMaterial}
            onSubmit={handleSubmit}
            loading={createLegMaterialStatus.loading}
          />
        );
      default:
        return (
          <TypMaterialuStepOne
            onChangeStep={onChangeStep}
            legislativnyMaterial={legislativnyMaterial}
            onUpdateField={updateField}
            loading={validateLegMaterialStatus.loading}
            getFieldErrorMessage={getFieldErrorMessage}
          />
        );
    }
  };

  return (
    <Row>
      <NavigationGuard
        headingModal="Naozaj si prajete ukončiť vytváranie procesu?"
        contentModal="Po ukončení vytvárania procesu prídete o zadané údaje vytváraného procesu."
      />
      <Col widthDesktop="three-quarters">
        <div className="legislativny-material-create">
          {renderActiveStepPage()}
        </div>
      </Col>
    </Row>
  );
};

export default LegislativnyMaterialCreate;
